import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * Page "Communication" côté organisateur d'événement.
 *
 * Trois usages :
 *   1. Voir la liste enrichie des inscrits (toutes infos : nom, prénom, mail,
 *      tél, niveau, statut paiement, statut questionnaire + réponses, +1...).
 *   2. Sélectionner des destinataires (ou utiliser un filtre / sélectionner
 *      tout le résultat filtré) et leur envoyer un message libre par email
 *      ou SMS, en y intégrant éventuellement un lien magique unique.
 *   3. Déclencher des relances pré-câblées : questionnaire / paiement (les
 *      destinataires sont automatiquement les inscrits concernés ; un lien
 *      magique vers la page de finalisation est inséré dans le message).
 */
const EventCommunication = ({ eventId: propEventId }) => {
  const eventId = useMemo(() => {
    if (propEventId) return propEventId;
    const match = window.location.pathname.match(/\/events\/(\d+)/);
    return match ? match[1] : null;
  }, [propEventId]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    payment_status: '',
    questionnaire: '',
    plus_one: '',
    status: ''
  });

  // Sélection multiple
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Modale de composition
  const [composer, setComposer] = useState(null);
  // composer: { preset, channel, subject, body, include_magic_link, magic_link_focus, mode: 'selection'|'filter'|'preset' }
  const [sending, setSending] = useState(false);

  // Historique
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('participants'); // participants | history
  const [toast, setToast] = useState(null);

  const csrf = () => document.querySelector('[name="csrf-token"]')?.content || '';

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ----- Fetch -------------------------------------------------------------

  const buildQuery = useCallback((f) => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([key, value]) => {
      if (value != null && value !== '') params.append(key, value);
    });
    return params.toString();
  }, []);

  const loadParticipants = useCallback(async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      setError(null);
      const qs = buildQuery(filters);
      const res = await fetch(`/api/v1/events/${eventId}/participants${qs ? `?${qs}` : ''}`, {
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || 'Chargement impossible');
      }
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId, filters, buildQuery]);

  const loadHistory = useCallback(async () => {
    if (!eventId) return;
    try {
      setHistoryLoading(true);
      const res = await fetch(`/api/v1/events/${eventId}/messages`, {
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const json = await res.json();
      if (json.success) setHistory(json.messages || []);
    } finally {
      setHistoryLoading(false);
    }
  }, [eventId]);

  useEffect(() => { loadParticipants(); }, [loadParticipants]);
  useEffect(() => { if (activeTab === 'history') loadHistory(); }, [activeTab, loadHistory]);

  // ----- Sélection ---------------------------------------------------------

  const participants = data?.participants || [];
  const stats        = data?.stats || {};
  const fields       = data?.questionnaire_fields || [];
  const perms        = data?.permissions || {};

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allFilteredSelected = participants.length > 0 && participants.every(p => selectedIds.has(p.registration_id));
  const toggleAllFiltered = () => {
    setSelectedIds(prev => {
      if (allFilteredSelected) {
        const next = new Set(prev);
        participants.forEach(p => next.delete(p.registration_id));
        return next;
      }
      const next = new Set(prev);
      participants.forEach(p => next.add(p.registration_id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // ----- Composer ----------------------------------------------------------

  const openComposer = (mode, presetOverride) => {
    const preset = presetOverride || 'free';
    const channelDefault = preset === 'free' ? 'email' : 'auto';
    const body = preset === 'reminder_questionnaire'
      ? `Bonjour {{first_name}},\n\nVotre inscription à "{{event_title}}" n'est pas encore finalisée : merci de répondre au questionnaire.\n\nFinalisez en un clic : {{magic_link}}\n\nLe {{event_date}} — {{event_location}}\n\nMerci !`
      : preset === 'reminder_payment'
        ? `Bonjour {{first_name}},\n\nVotre inscription à "{{event_title}}" nécessite un paiement pour être confirmée.\n\nRéglez en quelques secondes : {{magic_link}}\n\nMerci !`
        : '';
    const subject = preset === 'reminder_questionnaire'
      ? `Action requise : questionnaire pour {{event_title}}`
      : preset === 'reminder_payment'
        ? `Rappel paiement : {{event_title}}`
        : '';
    setComposer({
      mode,
      preset,
      channel: channelDefault,
      subject,
      body,
      include_magic_link: preset !== 'free',
      magic_link_focus: preset === 'reminder_questionnaire' ? 'questionnaire' : preset === 'reminder_payment' ? 'payment' : ''
    });
  };

  const closeComposer = () => setComposer(null);

  const buildRequestBody = (composerState) => {
    const body = {
      preset:             composerState.preset,
      channel:            composerState.channel,
      subject:            composerState.subject,
      body:               composerState.body,
      include_magic_link: composerState.include_magic_link,
      magic_link_focus:   composerState.magic_link_focus || null
    };
    if (composerState.mode === 'selection') {
      body.registration_ids = Array.from(selectedIds);
    } else if (composerState.mode === 'filter') {
      body.filters = filters;
    }
    // preset: pas d'IDs ni filtres → service détermine la cible naturellement
    return body;
  };

  const sendMessage = async () => {
    if (!composer) return;
    if (composer.mode === 'selection' && selectedIds.size === 0) {
      showToast('Sélectionnez au moins un destinataire', 'error');
      return;
    }
    try {
      setSending(true);
      const res = await fetch(`/api/v1/events/${eventId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify(buildRequestBody(composer))
      });
      const json = await res.json();
      if (json.success) {
        showToast(`✉️ ${json.results?.queued || 0} message(s) en cours d'envoi`);
        closeComposer();
        clearSelection();
        if (activeTab === 'history') loadHistory();
      } else {
        showToast(`❌ ${json.message || 'Envoi impossible'}`, 'error');
      }
    } catch (e) {
      showToast(`❌ ${e.message}`, 'error');
    } finally {
      setSending(false);
    }
  };

  const sendQuickReminders = async (type /* questionnaire | payment | both */) => {
    if (!confirm(`Envoyer une relance ${type === 'both' ? 'questionnaire ET paiement' : type} aux inscrits concernés ?`)) return;
    try {
      const res = await fetch(`/api/v1/events/${eventId}/messages/reminders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify({ type, channel: 'auto' })
      });
      const json = await res.json();
      if (json.success) {
        showToast(`📣 ${json.results.queued} relance(s) en cours d'envoi`);
        if (activeTab === 'history') loadHistory();
      } else {
        showToast(`❌ ${json.message || 'Envoi impossible'}`, 'error');
      }
    } catch (e) {
      showToast(`❌ ${e.message}`, 'error');
    }
  };

  // ----- Helpers UI --------------------------------------------------------

  const copyMagicLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      showToast('🔗 Lien magique copié');
    } catch {
      showToast('Impossible de copier le lien', 'error');
    }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  // ----- Render ------------------------------------------------------------

  if (!eventId) {
    return <div style={S.errorPage}>Identifiant d'événement manquant.</div>;
  }

  if (loading && !data) {
    return <div style={S.page}><div style={S.center}>Chargement…</div></div>;
  }

  if (error) {
    return <div style={S.page}><div style={S.center}>❌ {error}</div></div>;
  }

  const counts = {
    pendingQuestionnaire: participants.filter(p => p.finalization.needs_questionnaire).length,
    pendingPayment:       participants.filter(p => p.finalization.needs_payment).length
  };

  return (
    <div style={S.page}>
      {toast && (
        <div style={{ ...S.toast, background: toast.type === 'error' ? '#7c1c2a' : '#1a3a2a' }}>
          {toast.message}
        </div>
      )}

      <div style={S.container}>
        <div style={S.header}>
          <h1 style={S.h1}>💬 Communication — {data?.event?.title}</h1>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <a href={`/events/${eventId}/dashboard`} style={S.linkBtn}>← Retour au dashboard</a>
          </div>
        </div>

        {/* Bloc Relances rapides */}
        {perms.can_send_messages && (
          <div style={S.quickActions}>
            <div style={S.quickActionTitle}>📣 Relances express (lien magique inclus)</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={S.quickBtn(counts.pendingQuestionnaire === 0)} disabled={counts.pendingQuestionnaire === 0}
                onClick={() => sendQuickReminders('questionnaire')}>
                📝 Relancer le questionnaire ({counts.pendingQuestionnaire})
              </button>
              <button style={S.quickBtn(counts.pendingPayment === 0)} disabled={counts.pendingPayment === 0}
                onClick={() => sendQuickReminders('payment')}>
                💶 Relancer le paiement ({counts.pendingPayment})
              </button>
              <button style={S.quickBtn(counts.pendingQuestionnaire + counts.pendingPayment === 0)}
                disabled={counts.pendingQuestionnaire + counts.pendingPayment === 0}
                onClick={() => sendQuickReminders('both')}>
                🔁 Tout relancer ({counts.pendingQuestionnaire + counts.pendingPayment})
              </button>
              <button style={S.primaryBtn} onClick={() => openComposer('preset')}>
                ✏️ Composer un message libre…
              </button>
            </div>
            <div style={S.helpText}>
              Les relances utilisent le canal préféré de chaque destinataire (email si disponible, sinon SMS) et incluent un lien unique permettant de finaliser sans connexion.
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={S.tabs}>
          <button style={S.tab(activeTab === 'participants')} onClick={() => setActiveTab('participants')}>
            👥 Inscrits ({stats.total || 0})
          </button>
          <button style={S.tab(activeTab === 'history')} onClick={() => setActiveTab('history')}>
            📜 Historique des envois
          </button>
        </div>

        {activeTab === 'participants' && (
          <>
            {/* Stats compactes */}
            <div style={S.statsRow}>
              <StatChip label="Inscrits" value={stats.registered} color="#15803d" />
              <StatChip label="Attente" value={stats.waitlist} color="#f59e0b" />
              <StatChip label="Payés" value={stats.paid} color="#15803d" />
              <StatChip label="Non payés" value={stats.unpaid} color="#dc3545" />
              <StatChip label="Questionnaire ✓" value={stats.questionnaire_done} color="#15803d" />
              <StatChip label="Questionnaire ✗" value={stats.questionnaire_pending} color="#dc3545" />
              <StatChip label="+1" value={stats.plus_ones} color="#7c2837" />
              <StatChip label="Joignables par SMS" value={stats.with_phone} color="#3b82f6" />
            </div>

            {/* Filtres */}
            <div style={S.filterBar}>
              <input
                type="text" placeholder="🔍 Nom, prénom, email, téléphone…"
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                style={S.input}
              />
              <select style={S.select} value={filters.payment_status}
                onChange={e => setFilters(f => ({ ...f, payment_status: e.target.value }))}>
                <option value="">Paiement : tous</option>
                <option value="paid">Payés</option>
                <option value="unpaid">Non payés</option>
              </select>
              <select style={S.select} value={filters.questionnaire}
                onChange={e => setFilters(f => ({ ...f, questionnaire: e.target.value }))}>
                <option value="">Questionnaire : tous</option>
                <option value="completed">Complété</option>
                <option value="pending">En attente</option>
              </select>
              <select style={S.select} value={filters.plus_one}
                onChange={e => setFilters(f => ({ ...f, plus_one: e.target.value }))}>
                <option value="">+1 : tous</option>
                <option value="only">Uniquement +1</option>
                <option value="exclude">Sans les +1</option>
              </select>
              <select style={S.select} value={filters.status}
                onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="">Statut : tous</option>
                <option value="registered">Inscrits</option>
                <option value="waitlist">Liste d'attente</option>
                <option value="attended">Présents</option>
              </select>
            </div>

            {/* Barre d'action sélection */}
            {perms.can_send_messages && (
              <div style={S.actionBar}>
                <label style={S.checkboxRow}>
                  <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} />
                  <span>Sélectionner tout le résultat filtré ({participants.length})</span>
                </label>
                <div style={{ flex: 1 }} />
                <div style={S.selectionInfo}>{selectedIds.size} sélectionné(s)</div>
                <button style={S.secondaryBtn} onClick={clearSelection} disabled={selectedIds.size === 0}>
                  Vider
                </button>
                <button style={S.primaryBtn} onClick={() => openComposer('selection')} disabled={selectedIds.size === 0}>
                  ✉️ Envoyer aux sélectionnés ({selectedIds.size})
                </button>
                <button style={S.outlineBtn} onClick={() => openComposer('filter')}>
                  📨 Envoyer à tout le filtre ({participants.length})
                </button>
              </div>
            )}

            {/* Tableau */}
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}></th>
                    <th style={S.th}>Participant</th>
                    <th style={S.th}>Contact</th>
                    <th style={S.th}>Niveau</th>
                    <th style={S.th}>Statut</th>
                    <th style={S.th}>Paiement</th>
                    <th style={S.th}>Questionnaire</th>
                    <th style={S.th}>+1 / Invité par</th>
                    <th style={S.th}>Magic link</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map(p => (
                    <ParticipantRow
                      key={p.registration_id}
                      p={p}
                      fields={fields}
                      selected={selectedIds.has(p.registration_id)}
                      onToggle={() => toggleSelected(p.registration_id)}
                      onCopyLink={() => copyMagicLink(p.finalization.magic_link_url)}
                    />
                  ))}
                  {participants.length === 0 && (
                    <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', color: '#9e6575' }}>
                      Aucun inscrit ne correspond aux filtres.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'history' && (
          <HistoryPanel messages={history} loading={historyLoading} onRefresh={loadHistory} />
        )}
      </div>

      {composer && (
        <ComposerModal
          composer={composer}
          setComposer={setComposer}
          onClose={closeComposer}
          onSend={sendMessage}
          sending={sending}
          selectedCount={selectedIds.size}
          filteredCount={participants.length}
        />
      )}
    </div>
  );
};

// === Sous-composants =====================================================

const StatChip = ({ label, value, color }) => (
  <div style={{ ...S.statChip, borderColor: `${color}33` }}>
    <span style={{ color, fontWeight: 700, fontSize: '1.05rem' }}>{value ?? 0}</span>
    <span style={{ color: '#7a4050', fontSize: '.78rem' }}>{label}</span>
  </div>
);

const Pill = ({ color, bg, children }) => (
  <span style={{ background: bg, color, padding: '3px 8px', borderRadius: 6, fontSize: '.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const ParticipantRow = ({ p, fields, selected, onToggle, onCopyLink }) => {
  const [expanded, setExpanded] = useState(false);
  const answers = p.questionnaire.answers || {};
  return (
    <>
      <tr style={{ background: selected ? 'rgba(124,40,55,0.07)' : 'transparent', borderBottom: '1px solid rgba(124,40,55,0.10)' }}>
        <td style={S.td}>
          <input type="checkbox" checked={selected} onChange={onToggle} />
        </td>
        <td style={S.td}>
          <div style={{ fontWeight: 600, color: '#2c0e16', display: 'flex', alignItems: 'center', gap: 6 }}>
            {p.plus_one.is_plus_one && <Pill color="#fff" bg="#7c2837">+1</Pill>}
            {p.user.full_name}
          </div>
          <div style={{ color: '#7a4050', fontSize: '.78rem' }}>{p.user.email}</div>
        </td>
        <td style={S.td}>
          {p.user.phone ? (
            <div>
              📱 {p.user.phone}{' '}
              {p.user.phone_source === 'sms'
                ? (p.user.phone_verified
                    ? <Pill color="#15803d" bg="rgba(34,197,94,0.15)">vérifié</Pill>
                    : <Pill color="#f59e0b" bg="rgba(245,158,11,0.15)">non vérifié</Pill>)
                : <Pill color="#3b82f6" bg="rgba(59,130,246,0.15)" title="Issu du profil utilisateur">profil</Pill>}
            </div>
          ) : (
            <span style={{ color: '#9e6575', fontStyle: 'italic' }}>Pas de téléphone</span>
          )}
        </td>
        <td style={S.td}>
          <span style={{ background: '#f0e4e8', padding: '3px 8px', borderRadius: 6, fontSize: '.78rem', color: '#7a4050' }}>
            {p.user.level_label || p.user.level}
          </span>
        </td>
        <td style={S.td}>
          {p.status === 'registered' && <Pill color="#15803d" bg="rgba(34,197,94,0.15)">Inscrit</Pill>}
          {p.status === 'waitlist' && <Pill color="#f59e0b" bg="rgba(245,158,11,0.15)">Attente #{p.waitlist_position}</Pill>}
          {p.status === 'attended' && <Pill color="#3b82f6" bg="rgba(59,130,246,0.15)">Présent</Pill>}
        </td>
        <td style={S.td}>
          {!p.payment.required
            ? <Pill color="#7a4050" bg="#f0e4e8">Gratuit</Pill>
            : p.payment.paid
              ? <Pill color="#15803d" bg="rgba(34,197,94,0.15)">✅ {p.payment.amount}€</Pill>
              : <Pill color="#dc3545" bg="rgba(220,53,69,0.15)">⏳ {p.payment.amount}€</Pill>}
        </td>
        <td style={S.td}>
          {!p.questionnaire.required
            ? <Pill color="#7a4050" bg="#f0e4e8">N/A</Pill>
            : p.questionnaire.completed
              ? <button onClick={() => setExpanded(v => !v)} style={S.linkInline}>
                  ✅ Complété {expanded ? '▴' : '▾'}
                </button>
              : <Pill color="#dc3545" bg="rgba(220,53,69,0.15)">❌ En attente</Pill>}
        </td>
        <td style={S.td}>
          {p.plus_one.is_plus_one && p.plus_one.of_user_name && (
            <div style={{ fontSize: '.82rem', color: '#7c2837' }}>👤 invité de {p.plus_one.of_user_name}</div>
          )}
          {p.plus_one.active_plus_ones_count > 0 && (
            <div style={{ fontSize: '.82rem', color: '#7c2837' }}>+ {p.plus_one.active_plus_ones_count} accompagnant(s)</div>
          )}
          {p.invited_by && (
            <div style={{ fontSize: '.78rem', color: '#9e6575' }}>par {p.invited_by.name}</div>
          )}
        </td>
        <td style={S.td}>
          {p.finalization.magic_link_url ? (
            <button style={S.linkInline} onClick={onCopyLink} title="Copier le lien">🔗 Copier</button>
          ) : <span style={{ color: '#9e6575' }}>—</span>}
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: '#fdf6f8' }}>
          <td colSpan={9} style={{ padding: '12px 20px', borderBottom: '1px solid rgba(124,40,55,0.10)' }}>
            <div style={{ fontWeight: 700, color: '#7a4050', fontSize: '.82rem', marginBottom: 8 }}>Réponses au questionnaire</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
              {fields.map(f => {
                const value = answers[f.name];
                const display = Array.isArray(value) ? value.join(', ') : (value ?? '—');
                return (
                  <div key={f.name}>
                    <div style={{ color: '#9e6575', fontSize: '.72rem', textTransform: 'uppercase' }}>{f.label}</div>
                    <div style={{ color: '#2c0e16' }}>{String(display) || '—'}</div>
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const HistoryPanel = ({ messages, loading, onRefresh }) => (
  <div style={S.tableWrap}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid rgba(124,40,55,0.10)' }}>
      <div style={{ fontWeight: 700, color: '#2c0e16' }}>📜 Derniers envois ({messages.length})</div>
      <button style={S.secondaryBtn} onClick={onRefresh} disabled={loading}>{loading ? '…' : '↻ Rafraîchir'}</button>
    </div>
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Date</th>
          <th style={S.th}>Destinataire</th>
          <th style={S.th}>Type</th>
          <th style={S.th}>Canal</th>
          <th style={S.th}>Statut</th>
          <th style={S.th}>Sujet</th>
          <th style={S.th}>Envoyé par</th>
        </tr>
      </thead>
      <tbody>
        {messages.map(m => (
          <tr key={m.id} style={{ borderBottom: '1px solid rgba(124,40,55,0.08)' }}>
            <td style={S.td}>{m.created_at ? new Date(m.created_at).toLocaleString('fr-FR') : '—'}</td>
            <td style={S.td}><div style={{ color: '#2c0e16', fontWeight: 600 }}>{m.recipient?.name}</div><div style={{ color: '#7a4050', fontSize: '.78rem' }}>{m.recipient?.email}</div></td>
            <td style={S.td}>{labelForType(m.notification_type)}</td>
            <td style={S.td}>{labelForChannel(m.channel)}</td>
            <td style={S.td}>{labelForStatus(m.status, m.error_message)}</td>
            <td style={S.td}>{m.subject}</td>
            <td style={S.td}>{m.sent_by_name || '—'}</td>
          </tr>
        ))}
        {messages.length === 0 && !loading && (
          <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: '#9e6575' }}>Aucun envoi pour le moment.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const labelForType = (t) => ({
  organizer_message: '✉️ Message',
  reminder_questionnaire: '📝 Relance questionnaire',
  reminder_payment: '💶 Relance paiement'
})[t] || t;

const labelForChannel = (c) => ({ email: '📧 Email', sms: '📱 SMS', both: '📧+📱' })[c] || c;
const labelForStatus = (s, err) => ({
  pending:   <Pill color="#f59e0b" bg="rgba(245,158,11,0.15)">en attente</Pill>,
  sent:      <Pill color="#15803d" bg="rgba(34,197,94,0.15)">envoyé</Pill>,
  delivered: <Pill color="#15803d" bg="rgba(34,197,94,0.15)">délivré</Pill>,
  failed:    <Pill color="#dc3545" bg="rgba(220,53,69,0.15)">échec{err ? ` (${err.slice(0, 30)})` : ''}</Pill>
})[s] || s;

const ComposerModal = ({ composer, setComposer, onClose, onSend, sending, selectedCount, filteredCount }) => {
  const update = (k, v) => setComposer(c => ({ ...c, [k]: v }));
  const target = composer.mode === 'selection'
    ? `${selectedCount} destinataire(s) sélectionné(s)`
    : composer.mode === 'filter'
      ? `${filteredCount} destinataire(s) (résultat du filtre)`
      : composer.preset === 'reminder_questionnaire'
        ? `Inscrits dont le questionnaire est en attente`
        : composer.preset === 'reminder_payment'
          ? `Inscrits dont le paiement est en attente`
          : 'Tous les inscrits';

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>✉️ Composer un message</div>
            <div style={{ fontSize: '.82rem', color: '#7a4050', marginTop: 4 }}>{target}</div>
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={S.modalBody}>
          <label style={S.label}>Type d'envoi</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              { v: 'free', l: 'Message libre' },
              { v: 'reminder_questionnaire', l: 'Relance questionnaire' },
              { v: 'reminder_payment', l: 'Relance paiement' }
            ].map(o => (
              <button key={o.v}
                style={S.choiceBtn(composer.preset === o.v)}
                onClick={() => update('preset', o.v)}>{o.l}</button>
            ))}
          </div>

          <label style={S.label}>Canal</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {[
              { v: 'auto', l: '🤖 Auto (préférence utilisateur)' },
              { v: 'email', l: '📧 Email' },
              { v: 'sms',   l: '📱 SMS' },
              { v: 'both',  l: '📧 + 📱' }
            ].map(o => (
              <button key={o.v}
                style={S.choiceBtn(composer.channel === o.v)}
                onClick={() => update('channel', o.v)}>{o.l}</button>
            ))}
          </div>

          {(composer.channel === 'email' || composer.channel === 'both' || composer.channel === 'auto') && (
            <>
              <label style={S.label}>Sujet (email)</label>
              <input style={S.input} value={composer.subject}
                onChange={e => update('subject', e.target.value)}
                placeholder="Sujet de l'email" />
            </>
          )}

          <label style={S.label}>Message</label>
          <textarea style={S.textarea} rows={10} value={composer.body}
            onChange={e => update('body', e.target.value)}
            placeholder="Votre message…" />

          <div style={S.helpText}>
            Variables disponibles : <code>{'{{first_name}}'}</code>, <code>{'{{last_name}}'}</code>, <code>{'{{full_name}}'}</code>, <code>{'{{event_title}}'}</code>, <code>{'{{event_date}}'}</code>, <code>{'{{event_location}}'}</code>, <code>{'{{magic_link}}'}</code>
            {(composer.channel === 'sms' || composer.channel === 'both') && (
              <div style={{ marginTop: 6 }}>⚠️ Les SMS sont tronqués à 160 caractères.</div>
            )}
          </div>

          <label style={{ ...S.checkboxRow, marginTop: 14 }}>
            <input type="checkbox" checked={composer.include_magic_link}
              onChange={e => update('include_magic_link', e.target.checked)} />
            <span>Insérer un lien magique unique <code>{'{{magic_link}}'}</code> permettant de finaliser sans connexion</span>
          </label>

          {composer.include_magic_link && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              <span style={{ fontSize: '.82rem', color: '#7a4050' }}>Cible du lien :</span>
              {[
                { v: '', l: 'Page complète' },
                { v: 'questionnaire', l: 'Questionnaire' },
                { v: 'payment', l: 'Paiement' }
              ].map(o => (
                <button key={o.v}
                  style={S.choiceBtnSmall((composer.magic_link_focus || '') === o.v)}
                  onClick={() => update('magic_link_focus', o.v)}>{o.l}</button>
              ))}
            </div>
          )}
        </div>

        <div style={S.modalFooter}>
          <button style={S.secondaryBtn} onClick={onClose} disabled={sending}>Annuler</button>
          <button style={S.primaryBtn} onClick={onSend} disabled={sending}>
            {sending ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// === Styles ==============================================================

const S = {
  page: { padding: '160px 24px 40px', minHeight: '100vh', background: '#f7eef2', fontFamily: 'inherit' },
  errorPage: { padding: 160, textAlign: 'center', color: '#7c2837', fontSize: '1.1rem' },
  container: { maxWidth: 1400, margin: '0 auto' },
  center: { textAlign: 'center', color: '#2c0e16', fontSize: '1.1rem', padding: 40 },
  toast: { position: 'fixed', top: 24, right: 24, color: '#fff', padding: '14px 24px', borderRadius: 12, zIndex: 9999, fontWeight: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 24 },
  h1: { color: '#2c0e16', fontSize: 'clamp(1.4rem, 2.6vw, 2rem)', fontWeight: 800, margin: 0 },
  linkBtn: { background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 18px', borderRadius: 10, textDecoration: 'none', fontWeight: 600 },
  quickActions: { background: '#fff', border: '1px solid rgba(124,40,55,0.15)', borderRadius: 14, padding: 16, marginBottom: 20 },
  quickActionTitle: { fontWeight: 700, color: '#2c0e16', marginBottom: 10 },
  quickBtn: (disabled) => ({
    background: disabled ? '#f0e4e8' : '#fff',
    color:      disabled ? '#9e6575' : '#7c2837',
    border:     '1px solid rgba(124,40,55,0.30)',
    padding: '10px 16px', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600
  }),
  primaryBtn: { background: 'linear-gradient(135deg, #7c2837 0%, #a8384e 100%)', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 10, cursor: 'pointer', fontWeight: 700 },
  secondaryBtn: { background: '#fdf6f8', color: '#2c0e16', border: '1px solid rgba(124,40,55,0.25)', padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 },
  outlineBtn: { background: 'transparent', color: '#7c2837', border: '1px solid rgba(124,40,55,0.40)', padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 },
  helpText: { color: '#7a4050', fontSize: '.82rem', marginTop: 10 },
  tabs: { display: 'flex', gap: 8, marginTop: 4, marginBottom: 16 },
  tab: (active) => ({
    background: active ? 'linear-gradient(135deg, #7c2837 0%, #a8384e 100%)' : 'transparent',
    color: active ? '#fff' : '#2c0e16',
    border: active ? 'none' : '1px solid rgba(124,40,55,0.25)',
    padding: '10px 18px', borderRadius: 10, cursor: 'pointer', fontWeight: 700
  }),
  statsRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  statChip: { display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff', border: '1px solid', borderRadius: 10, padding: '8px 14px', minWidth: 90 },
  filterBar: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
  input: { background: '#fff', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 14px', borderRadius: 10, outline: 'none', minWidth: 220, flex: '1 1 220px', fontSize: '.92rem' },
  select: { background: '#fff', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 14px', borderRadius: 10, outline: 'none', fontSize: '.92rem' },
  actionBar: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: '#fff', border: '1px solid rgba(124,40,55,0.15)', borderRadius: 12, padding: '10px 14px', marginBottom: 12 },
  checkboxRow: { display: 'flex', gap: 8, alignItems: 'center', color: '#2c0e16', cursor: 'pointer', fontSize: '.92rem' },
  selectionInfo: { color: '#7a4050', fontSize: '.88rem', fontWeight: 600 },
  tableWrap: { background: '#fff', border: '1px solid rgba(124,40,55,0.12)', borderRadius: 14, overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '.88rem' },
  th: { textAlign: 'left', padding: '12px 14px', background: '#f0e4e8', color: '#7a4050', fontWeight: 700, textTransform: 'uppercase', fontSize: '.74rem', letterSpacing: '.05em', whiteSpace: 'nowrap' },
  td: { padding: '12px 14px', verticalAlign: 'top', color: '#2c0e16' },
  linkInline: { background: 'transparent', border: 'none', color: '#7c2837', cursor: 'pointer', textDecoration: 'underline', fontSize: '.85rem', padding: 0, fontWeight: 600 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(44,14,22,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998, padding: 16 },
  modal: { background: '#fff', borderRadius: 16, maxWidth: 720, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' },
  modalHeader: { padding: '18px 22px', borderBottom: '1px solid rgba(124,40,55,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, color: '#2c0e16' },
  modalBody:   { padding: '18px 22px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 4 },
  modalFooter: { padding: '14px 22px', borderTop: '1px solid rgba(124,40,55,0.15)', display: 'flex', justifyContent: 'flex-end', gap: 10 },
  closeBtn:    { background: 'transparent', border: 'none', color: '#7a4050', fontSize: '1.2rem', cursor: 'pointer' },
  label:       { color: '#7a4050', fontSize: '.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', margin: '6px 0 4px' },
  textarea:    { background: '#fff', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 14px', borderRadius: 10, outline: 'none', fontFamily: 'inherit', fontSize: '.92rem', resize: 'vertical' },
  choiceBtn:   (active) => ({
    background: active ? '#7c2837' : '#fdf6f8',
    color: active ? '#fff' : '#2c0e16',
    border: active ? '1px solid #7c2837' : '1px solid rgba(124,40,55,0.25)',
    padding: '8px 14px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: '.88rem'
  }),
  choiceBtnSmall: (active) => ({
    background: active ? '#7c2837' : 'transparent',
    color: active ? '#fff' : '#7c2837',
    border: '1px solid rgba(124,40,55,0.40)',
    padding: '4px 10px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '.78rem'
  })
};

export default EventCommunication;
